import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation, FilterType } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import usePartnerApi from "hooks/api/private/usePartnerApi";
import useSnackbar from "hooks/useSnackbar";

import { PartnerListItem } from "types/api/partner";

export type PartnerListHook = {
    navigateToNewPartner: () => void;
    onSearchFilterChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    dataTableHook: DataTableHook<PartnerListItem>;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    staticFilterHook: StaticTableFilterHook;
    alertConfirmHook: AlertConfirmHookType;
};

function usePartnerList(): PartnerListHook {
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    title: {
                        key: "title",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "partner-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);
                newForm.search.title = "" + event.target.value;
                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            language: {
                key: "language",
                operation: FilterOperation.Eq,
                defaultValue: "it",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "partner-list-static",
        memoizedFieldsForStaticFilters,
    );

    const { getAllPartners, deletePartner } = usePartnerApi();

    const navigate = useNavigate();

    const navigateToEditPartner = React.useCallback(
        (row: PartnerListItem) => {
            navigate(makeUrl(`${row.id}/modifica`));
        },
        [navigate],
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina partners",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deletePartnerButton = React.useCallback(
        (row: PartnerListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare il partners?", () => {
                const [apiCall] = deletePartner({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Partners eliminato con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione del partners"));
            });
        },
        [enqueueSuccessSnackbar, setTriggerReloadTable, enqueueErrorSnackbar, openConfirmAlert, deletePartner],
    );

    const columns: Array<Column<PartnerListItem>> = React.useMemo(
        () => [
            {
                id: "title",
                header: "Titolo",
                render: (row: PartnerListItem) => row.title,
            },
            {
                id: "link",
                header: "Link",
                render: (row: PartnerListItem) => row.link ?? "-",
            },
            {
                id: "enabled",
                header: "Visibile",
                width: "130px",
                render: (row: PartnerListItem) => (row.enabled ? "Visibile" : "Non visibile"),
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: PartnerListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditPartner}
                        onDelete={deletePartnerButton}
                    />
                ),
            },
        ],
        [navigateToEditPartner, deletePartnerButton],
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters],
    );

    const dataTableHook = useDataTableWithMemory<PartnerListItem>({
        apiCall: getAllPartners,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "partner-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();
            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewPartner = React.useCallback(() => {
        navigate("/partners/nuovo");
    }, [navigate]);

    return React.useMemo(
        (): PartnerListHook => ({
            onSearchFilterChange,
            navigateToNewPartner,
            filterSearchHook,
            staticFilterHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [onSearchFilterChange, navigateToNewPartner, filterSearchHook, alertConfirmHook, staticFilterHook, dataTableHook],
    );
}

export default usePartnerList;
